/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/employees.ts
 * 员工管理API服务（Mock 优先，联调时切换 VITE_USE_MOCK=false）
 */

import { BaseApiService } from './base'
import { httpClient } from './http'
import type {
  Employee,
  EmployeeFilters,
  EmployeeFormData,
  EmployeeStatsData,
  PaginatedEmployeeResponse,
  OrgTreeNode
} from '../types/employee'

/**
 * 批量重置密码响应（后端 batch-reset-password 接口）
 */
export interface BatchResetPasswordResult {
  full_name?: string | null
  username: string | null
  success: boolean
  new_password?: string
  error?: string
  /** @deprecated 仅旧版兼容，CSV 不再输出 */
  user_id?: number | string
}

export interface BatchResetPasswordResponse {
  async?: boolean
  task_id?: number
  success_count: number
  fail_count: number
  total: number
  results: BatchResetPasswordResult[]
  password_mode?: 'uniform' | 'random'
}

export interface BatchResetPasswordTaskStatus {
  task_id: number
  status: string
  password_mode: string
  total_employees: number
  processed_employees: number
  success_count: number
  fail_count: number
  error_message: string
}

export interface BatchResetPasswordRequestOptions {
  /** 统一密码模式下必填 */
  newPassword?: string
  /** uniform=统一密码；random=每人随机（推荐风控止损） */
  passwordMode?: 'uniform' | 'random'
}

/** 同步请求超时（uniform 大批量仍较快；random ≤30 人同步） */
const BATCH_RESET_PASSWORD_TIMEOUT_MS = 180_000

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

class EmployeeApiService extends BaseApiService<Employee, EmployeeFormData, Partial<EmployeeFormData>> {
  protected readonly endpoint = 'employees/'

  /**
   * 获取员工列表（支持分页/排序/筛选）
   * 输入：EmployeeFilters + 分页参数
   * 输出：PaginatedEmployeeResponse 分页结果
   */
  async getEmployees(params?: EmployeeFilters & {
    page?: number
    page_size?: number
    ordering?: string
  }): Promise<PaginatedEmployeeResponse> {
    try {
      // 参数适配：keyword -> search（后端筛选参数名）
      const query: Record<string, unknown> = { ...(params || {}) }
      const keyword = query['keyword']
      if (typeof keyword === 'string' && keyword.trim() !== '') {
        query['search'] = keyword
        delete query['keyword']
      }
      // 参数适配：workplace_ids[] -> workplace（单值过滤）
      const workplaceIds = query['workplace_ids']
      if (Array.isArray(workplaceIds) && workplaceIds.length > 0) {
        query['workplace'] = workplaceIds[0]
        delete query['workplace_ids']
      }
      // 清理空参数，避免 undefined/空串干扰服务端过滤
      Object.keys(query).forEach((k) => {
        const v = query[k]
        if (v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) delete query[k]
      })

      const response = await httpClient
        .getAxiosInstance()
        .get<PaginatedEmployeeResponse | { data: PaginatedEmployeeResponse }>(this.endpoint, { params: query })
      const raw: unknown = response.data
      const normalized: unknown = (() => {
        if (isRecord(raw) && Array.isArray(raw.results)) return raw
        if (isRecord(raw) && isRecord(raw.data) && Array.isArray(raw.data.results)) return raw.data
        return raw
      })()
      if (!isRecord(normalized) || !Array.isArray(normalized.results)) {
        throw new Error('Invalid employee list response shape')
      }
      // 开发环境且列表为空：回退 Mock 以便联调表格交互
      const result = normalized as unknown as PaginatedEmployeeResponse
      if (import.meta.env.VITE_USE_MOCK !== 'false' && result.results.length === 0) {
        return this.getMockEmployees(params)
      }
      return result
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockEmployees(params)
      }
      throw error
    }
  }

  /**
   * 获取员工详情
   * 输入：员工ID
   * 输出：Employee 详情
   */
  async getEmployeeById(id: string): Promise<Employee> {
    try {
      return await httpClient.get<Employee>(`${this.endpoint}${id}/`)
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockEmployeeById(id)
      }
      throw error
    }
  }

  /**
   * ⭐ M11 新增：按部门分组获取员工列表（代点专用）
   * 输入：dining_workplace_id 用餐职场ID（可选）
   * 输出：按部门分组的员工列表
   */
  async getEmployeesByDepartment(params?: {
    dining_workplace_id?: string | number
    include_balance?: boolean
  }): Promise<{
    departments: Array<{
      id: number
      name: string
      employee_count: number
      employees: Employee[]
    }>
    ungrouped: Employee[]
  }> {
    const response = await httpClient
      .getAxiosInstance()
      .get<unknown>(`${this.endpoint}by-department/`, { params })
    // 提取 data 字段（后端统一响应包装）
    const raw: unknown = (response as unknown as { data?: unknown }).data
    const top = toRecord(raw)
    const data = top.data ?? raw
    if (isRecord(data) && Array.isArray(data.departments) && Array.isArray(data.ungrouped)) {
      return data as unknown as { departments: Array<{ id: number; name: string; employee_count: number; employees: Employee[] }>; ungrouped: Employee[] }
    }
    return { departments: [], ungrouped: [] }
  }

  /**
   * 创建员工
   */
  async createEmployee(data: EmployeeFormData): Promise<Employee> {
    try {
      const payload = this.transformFormDataToPayload(data)
      return await httpClient.post<Employee>(this.endpoint, payload)
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.createMockEmployee(data)
      }
      throw error
    }
  }

  /**
   * 更新员工
   */
  async updateEmployee(id: string, data: Partial<EmployeeFormData>): Promise<Employee> {
    try {
      const payload = this.transformFormDataToPayload(data)
      return await httpClient.patch<Employee>(`${this.endpoint}${id}/`, payload)
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.updateMockEmployee(id, data)
      }
      throw error
    }
  }

  /**
   * 删除员工
   */
  async deleteEmployee(id: string): Promise<void> {
    try {
      await httpClient.delete(`${this.endpoint}${id}/`)
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.deleteMockEmployee(id)
      }
      throw error
    }
  }

  /**
   * 获取员工统计（指标区）
   */
  async getEmployeeStats(filters?: EmployeeFilters): Promise<EmployeeStatsData> {
    try {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockEmployeeStats(filters)
      }
      return await httpClient.get<EmployeeStatsData>(`${this.endpoint}stats/`, { params: filters })
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockEmployeeStats(filters)
      }
      throw error
    }
  }

  /**
   * 批量企业补贴余额充值
   */
  async batchRechargeEnterpriseSubsidy(items: Array<{ employee_id: string; amount: number; remark?: string }>): Promise<{ success: string[]; failed: Array<{ employee_id: string; reason: string }> }> {
    try {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.mockBatchBalanceAction(items.map(i => i.employee_id), 'recharge')
      }
      return await httpClient.post(`${this.endpoint}batch-recharge/`, { items })
    } catch (e) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.mockBatchBalanceAction(items.map(i => i.employee_id), 'recharge')
      }
      throw e
    }
  }

  /**
   * 批量企业补贴余额清零
   */
  async batchClearEnterpriseSubsidy(employeeIds: string[]): Promise<{ success: string[]; failed: Array<{ employee_id: string; reason: string }> }> {
    try {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.mockBatchBalanceAction(employeeIds, 'clear')
      }
      return await httpClient.post(`${this.endpoint}batch-clear/`, { employee_ids: employeeIds })
    } catch (e) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.mockBatchBalanceAction(employeeIds, 'clear')
      }
      throw e
    }
  }

  /**
   * @deprecated meal_number 已废弃，此方法不再使用
   */
  async checkMealNumberUnique(_enterpriseId: string, _mealNumber: string): Promise<{ unique: boolean; conflict_id?: string }> {
    void _enterpriseId
    void _mealNumber
    return { unique: true }
  }

  /**
   * 导出员工（当前筛选）
   */
  async exportEmployees(params?: EmployeeFilters): Promise<Blob> {
    try {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        const csv = 'id,username,full_name\nemp_1,user_1,员工1\n'
        return new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      }
      const res = await httpClient.getAxiosInstance().get(`${this.endpoint}export/`, { params, responseType: 'blob' })
      return res.data
    } catch (e) {
      const csv = 'id,username,full_name\n'
      return new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    }
  }

  /**
   * 批量重置员工密码（random >30 人自动走 Celery 异步）
   *
   * POST /api/v1/employees/batch-reset-password/
   */
  async batchResetPassword(
    employeeIds: (string | number)[],
    options?: BatchResetPasswordRequestOptions
  ): Promise<BatchResetPasswordResponse> {
    const ids = employeeIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
    if (ids.length === 0) {
      return { success_count: 0, fail_count: 0, total: 0, results: [] }
    }

    const payload: Record<string, unknown> = { employee_ids: ids }
    const passwordMode = options?.passwordMode ?? (options?.newPassword ? 'uniform' : undefined)
    if (passwordMode) payload.password_mode = passwordMode
    if (passwordMode === 'uniform' && options?.newPassword) {
      payload.new_password = options.newPassword
    }

    const raw = await httpClient.getAxiosInstance().post(
      `${this.endpoint}batch-reset-password/`,
      payload,
      { timeout: BATCH_RESET_PASSWORD_TIMEOUT_MS, validateStatus: () => true }
    )
    const status = raw.status
    const body = toRecord(raw.data)
    const data = toRecord(body.data)

    if (status === 202 || data.async === true) {
      return {
        async: true,
        task_id: Number(data.task_id) || undefined,
        total: Number(data.total) || ids.length,
        success_count: 0,
        fail_count: 0,
        results: [],
        password_mode: (data.password_mode as 'uniform' | 'random') || passwordMode,
      }
    }

    if (status >= 400) {
      throw new Error(String(body.message || '批量重置密码失败'))
    }

    return {
      async: false,
      success_count: Number(data.success_count) || 0,
      fail_count: Number(data.fail_count) || 0,
      total: Number(data.total) || ids.length,
      results: Array.isArray(data.results) ? (data.results as BatchResetPasswordResult[]) : [],
      password_mode: passwordMode,
    }
  }

  /**
   * 轮询批量重置密码异步任务
   *
   * GET /api/v1/employees/batch-reset-password/tasks/{task_id}/
   */
  async getBatchResetPasswordTask(taskId: number): Promise<BatchResetPasswordTaskStatus> {
    const data = await httpClient.get<BatchResetPasswordTaskStatus>(
      `${this.endpoint}batch-reset-password/tasks/${taskId}/`
    )
    return data
  }

  /**
   * 下载异步任务密码清单 CSV
   *
   * GET /api/v1/employees/batch-reset-password/tasks/{task_id}/download/
   */
  async downloadBatchResetPasswordCsv(taskId: number): Promise<void> {
    const response = await httpClient.getAxiosInstance().get(
      `${this.endpoint}batch-reset-password/tasks/${taskId}/download/`,
      { responseType: 'blob' }
    )
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `employee-passwords_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  /**
   * 导入员工（先预检再提交，此处提供简单直传接口）
   */
  async importEmployees(file: File): Promise<{ created: number; updated: number; failed: Array<{ row: number; reason: string }> }> {
    try {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return { created: 10, updated: 0, failed: [] }
      }
      return await httpClient.upload(`${this.endpoint}import/`, file)
    } catch (e) {
      return { created: 0, updated: 0, failed: [{ row: 1, reason: '格式错误' }] }
    }
  }

  /**
   * 获取组织树（懒加载）
   * - root: 返回运营商→企业的根节点集合
   * - children: 传入父节点 id，懒加载其子级（企业→一级部门→二级部门）
   */
  async getOrgTreeNodes(parentId?: string): Promise<OrgTreeNode[]> {
    try {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockOrgTreeNodes(parentId)
      }
      const url = parentId ? `${this.endpoint}org-tree/${parentId}/children/` : `${this.endpoint}org-tree/`
      return await httpClient.get<OrgTreeNode[]>(url)
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockOrgTreeNodes(parentId)
      }
      throw error
    }
  }

  // ==================== Mock 数据 ====================

  private async getMockEmployees(params?: Record<string, unknown>): Promise<PaginatedEmployeeResponse> {
    await new Promise((r) => setTimeout(r, 250))

    // ⚠️ 强制：即使是 Mock 种子数据，也必须使用后端真实餐段 code（契约唯一真源）
    // 约定的稳定 code（可通过“常量目录管理→餐段类别”启用/停用控制）：
    // - ZC0002 早餐
    // - WC0003 午餐
    // - WC0004 晚餐
    const MOCK_MEAL_SEGMENT_CODES = ['ZC0002', 'WC0003', 'WC0004']

    const all: Employee[] = Array.from({ length: 120 }).map((_, i) => {
      const e: Employee = {
        id: `emp_${i + 1}`,
        username: `user_${i + 1}`,
        full_name: `员工${i + 1}`,
        gender: (i % 3 === 0 ? 'male' : i % 3 === 1 ? 'female' : 'unknown'),
        enterprise: { id: `ent_${(i % 8) + 1}`, name: `企业${(i % 8) + 1}` },
        department: { id: `dept_${(i % 16) + 1}`, name: `部门${(i % 16) + 1}`, parent_id: i % 2 === 0 ? null : `dept_${(i % 8) + 1}` },
        workplaces: [{ id: `wk_${(i % 5) + 1}`, name: `职场${(i % 5) + 1}` }],
        meal_segments: MOCK_MEAL_SEGMENT_CODES.filter((_, idx) => (i + idx) % 2 === 0),
        phone_number: `1380000${String(1000 + i)}`,
        email: `user_${i + 1}@example.com`,
        enterprise_subsidy_balance: Math.floor(Math.random() * 10000) / 100,
        personal_balance: Math.floor(Math.random() * 5000) / 100,
        employee_no: String(100000 + i),
        is_admin: i % 17 === 0,
        status: i % 9 === 0 ? 'disabled' : 'active',
        registered_at: new Date(Date.now() - i * 86400000).toISOString(),
        last_login: i % 7 === 0 ? null : new Date(Date.now() - i * 3600000).toISOString(),
        operator_name: i % 3 === 0 ? '系统' : '管理员'
      }
      return e
    })

    // 本地筛选
    let list = all
    const p = toRecord(params)
    const kw = typeof p.keyword === 'string' ? p.keyword.trim() : ''
    if (kw) {
      const lower = kw.toLowerCase()
      list = list.filter((e) =>
        e.username.toLowerCase().includes(lower) ||
        e.full_name.includes(kw) ||
        (e.employee_no || '').includes(kw)
      )
    }
    if (typeof p.enterprise_id === 'string') list = list.filter((e) => e.enterprise?.id === p.enterprise_id)
    if (Array.isArray(p.department_ids) && p.department_ids.length) {
      const set = new Set(p.department_ids.filter((x): x is string => typeof x === 'string'))
      list = list.filter((e) => e.department && set.has(e.department.id))
    }
    if (Array.isArray(p.workplace_ids) && p.workplace_ids.length) {
      const set = new Set(p.workplace_ids.filter((x): x is string => typeof x === 'string'))
      list = list.filter((e) => (e.workplaces || []).some((w) => set.has(w.id)))
    }
    if (Array.isArray(p.meal_segments) && p.meal_segments.length) {
      const set = new Set(p.meal_segments.filter((x): x is string => typeof x === 'string'))
      list = list.filter((e) => (e.meal_segments || []).some((s) => set.has(s)))
    }
    if (typeof p.is_admin === 'boolean') list = list.filter((e) => Boolean(e.is_admin) === p.is_admin)
    if (typeof p.status === 'string') list = list.filter((e) => e.status === p.status)
    if (typeof p.registered_start === 'string' || typeof p.registered_end === 'string') {
      const start = typeof p.registered_start === 'string' ? new Date(p.registered_start).getTime() : -Infinity
      const end = typeof p.registered_end === 'string' ? new Date(p.registered_end).getTime() : Infinity
      list = list.filter((e) => {
        const t = e.registered_at ? new Date(e.registered_at).getTime() : 0
        return t >= start && t <= end
      })
    }

    // 排序（与后端对齐：-field 降序）
    const ordering = typeof p.ordering === 'string' ? p.ordering : undefined
    if (ordering) {
      const desc = ordering.startsWith('-')
      const key = desc ? ordering.slice(1) : ordering
      list = list.slice().sort((a, b) => {
        const av = toRecord(a as unknown)[key]
        const bv = toRecord(b as unknown)[key]
        if (av === bv) return 0
        if (typeof av === 'number' && typeof bv === 'number') {
          return (av > bv ? 1 : -1) * (desc ? -1 : 1)
        }
        const as = String(av ?? '')
        const bs = String(bv ?? '')
        return (as > bs ? 1 : -1) * (desc ? -1 : 1)
      })
    }

    // 分页
    const page = typeof p.page === 'number' ? p.page : 1
    const pageSize = typeof p.page_size === 'number' ? p.page_size : 20
    const start = (page - 1) * pageSize
    const end = start + pageSize
    const results = list.slice(start, end)

    return {
      results,
      count: list.length,
      next: end < list.length ? `page=${page + 1}` : null,
      previous: page > 1 ? `page=${page - 1}` : null
    }
  }

  private async getMockEmployeeById(id: string): Promise<Employee> {
    const list = await this.getMockEmployees({ page: 1, page_size: 120 })
    const found = list.results.find((e) => e.id === id)
    if (!found) throw new Error(`Employee ${id} not found`)
    return found
  }

  private async createMockEmployee(data: EmployeeFormData): Promise<Employee> {
    await new Promise((r) => setTimeout(r, 200))
    return {
      id: `emp_${Date.now()}`,
      // username/初始密码由后端生成；mock 场景兜底生成一个可用值避免类型报错
      username: data.username ?? `auto_${Date.now()}`,
      full_name: data.full_name,
      gender: data.gender || 'unknown',
      enterprise: data.enterprise_id ? { id: data.enterprise_id, name: `企业${data.enterprise_id}` } : undefined,
      department: data.department_id ? { id: data.department_id, name: `部门${data.department_id}` } : undefined,
      workplaces: (data.workplace_ids || []).map((id) => ({ id, name: `职场${id}` })),
      meal_segments: data.allowed_meal_segments || [],
      phone_number: data.phone_number,
      email: data.email,
      enterprise_subsidy_balance: 0,
      personal_balance: 0,
      employee_no: data.employee_no,
      ic_card_number: data.ic_card_number,
      is_admin: Boolean(data.is_admin),
      employee_role: data.employee_role ?? 'staff',
      status: data.status || 'active',
      registered_at: new Date().toISOString(),
      last_login: null,
      operator_name: '系统'
    }
  }

  private async updateMockEmployee(id: string, data: Partial<EmployeeFormData>): Promise<Employee> {
    const origin = await this.getMockEmployeeById(id)
    return {
      ...origin,
      username: data.username ?? origin.username,
      full_name: data.full_name ?? origin.full_name,
      gender: data.gender ?? origin.gender,
      employee_role: data.employee_role ?? origin.employee_role,
      enterprise: data.enterprise_id ? { id: data.enterprise_id, name: `企业${data.enterprise_id}` } : origin.enterprise,
      department: data.department_id ? { id: data.department_id, name: `部门${data.department_id}` } : origin.department,
      workplaces: data.workplace_ids ? data.workplace_ids.map((wid) => ({ id: wid, name: `职场${wid}` })) : origin.workplaces,
      meal_segments: data.allowed_meal_segments ?? origin.meal_segments,
      phone_number: data.phone_number ?? origin.phone_number,
      email: data.email ?? origin.email,
      employee_no: data.employee_no ?? origin.employee_no,
      ic_card_number: data.ic_card_number ?? origin.ic_card_number,
      is_admin: data.is_admin ?? origin.is_admin,
      status: data.status ?? origin.status,
      operator_name: '系统'
    }
  }

  private async deleteMockEmployee(_id: string): Promise<void> {
    await new Promise((r) => setTimeout(r, 150))
    void _id
  }

  private async getMockEmployeeStats(filters?: EmployeeFilters): Promise<EmployeeStatsData> {
    await new Promise((r) => setTimeout(r, 120))
    // 简单根据筛选扰动数值，便于联调
    const base = 120
    const factor = (filters?.status === 'active' ? 1.0 : 0.9) * (filters?.is_admin ? 0.6 : 1)
    const total = Math.floor(base * factor)
    const active = Math.floor(total * 0.78)
    const disabled = total - active
    const addedThisMonth = Math.floor(total * 0.03)
    const disabledThisMonth = Math.floor(total * 0.01)
    return {
      total,
      active,
      disabled,
      added_this_month: addedThisMonth,
      disabled_this_month: disabledThisMonth
    }
  }

  private async getMockOrgTreeNodes(parentId?: string): Promise<OrgTreeNode[]> {
    await new Promise((r) => setTimeout(r, 120))
    // 根：运营商下企业
    if (!parentId) {
      return Array.from({ length: 5 }).map((_, i) => ({
        id: `ent_${i + 1}`,
        label: `企业${i + 1}`,
        type: 'enterprise' as const,
        has_children: true
      }))
    }
    // 企业 → 一级部门
    if (parentId.startsWith('ent_')) {
      return Array.from({ length: 6 }).map((_, i) => ({
        id: `${parentId}_dept_${i + 1}`,
        label: `一级部门${i + 1}`,
        type: 'department' as const,
        parent_id: parentId,
        has_children: true
      }))
    }
    // 一级部门 → 二级部门
    if (parentId.includes('_dept_') && (parentId.match(/_dept_/g) || []).length === 1) {
      return Array.from({ length: 4 }).map((_, i) => ({
        id: `${parentId}_sub_${i + 1}`,
        label: `二级部门${i + 1}`,
        type: 'department' as const,
        parent_id: parentId,
        has_children: false
      }))
    }
    return []
  }

  private async mockBatchBalanceAction(ids: string[], _action: 'recharge'|'clear'): Promise<{ success: string[]; failed: Array<{ employee_id: string; reason: string }> }> {
    await new Promise((r) => setTimeout(r, 200))
    void _action
    // 单人操作默认成功，避免联调阶段产生误导性的权限错误
    if (ids.length === 1) {
      return { success: [ids[0]], failed: [] }
    }
    // 批量操作模拟少量失败
    const failed: Array<{ employee_id: string; reason: string }> = []
    const success: string[] = []
    ids.forEach((id, idx) => {
      if (idx % 11 === 0) failed.push({ employee_id: id, reason: '企业未授权此操作' })
      else success.push(id)
    })
    return { success, failed }
  }

  /**
   * 表单数据 → API 载荷转换
   */
  private transformFormDataToPayload(data: Partial<EmployeeFormData>): Record<string, unknown> {
    const payload: Record<string, unknown> = {}
    const d = toRecord(data as unknown)
    const toInt = (v: unknown): number | undefined => {
      if (v === '' || v === null || v === undefined) return undefined
      if (typeof v === 'number') return v
      const s = String(v).trim()
      return /^\d+$/.test(s) ? parseInt(s, 10) : undefined
    }
    const normStr = (v: unknown): string | undefined => {
      if (v === null || v === undefined) return undefined
      const s = String(v).trim()
      return s.length > 0 ? s : undefined
    }
    if (d.enterprise_id !== undefined) {
      const v = toInt(d.enterprise_id)
      if (v !== undefined) payload['enterprise_id'] = v
    }
    if (d.department_id !== undefined) {
      const v = toInt(d.department_id)
      if (v !== undefined) payload['department_id'] = v
    }
    // ✅ 对齐契约：后端写入侧接受 workplace_ids / dining_workplace_ids（均为 int 列表）
    if (Array.isArray(d.workplace_ids)) {
      const ids = d.workplace_ids.map(toInt).filter((x): x is number => x !== undefined)
      if (ids.length > 0) payload['workplace_ids'] = ids
    }
    if (Array.isArray(d.dining_workplace_ids)) {
      const ids = d.dining_workplace_ids.map(toInt).filter((x): x is number => x !== undefined)
      if (ids.length > 0) payload['dining_workplace_ids'] = ids
    }
    if (d.allowed_meal_segments !== undefined) payload['allowed_meal_segments'] = d.allowed_meal_segments
    if (d.consumption_types !== undefined) payload['consumption_types'] = d.consumption_types
    // 员工角色字段：支持 employee_role / employee_role_code（取其一）
    const role = d.employee_role ?? d.employee_role_code
    if (role !== undefined) payload['employee_role'] = role
    const username = normStr(d.username)
    if (username !== undefined) payload['username'] = username
    const fullName = normStr(d.full_name)
    if (fullName !== undefined) payload['full_name'] = fullName
    if (d.gender !== undefined) payload['gender'] = d.gender
    const phone = normStr(d.phone_number)
    if (phone !== undefined) payload['phone_number'] = phone
    const email = normStr(d.email)
    if (email !== undefined) payload['email'] = email
    const employeeNo = normStr(d.employee_no)
    if (employeeNo !== undefined) payload['employee_no'] = employeeNo
    const ic = normStr(d.ic_card_number)
    if (ic !== undefined) payload['ic_card_number'] = ic
    if (d.is_admin !== undefined) payload['is_admin'] = d.is_admin
    if (d.smart_auto_order_enabled !== undefined) payload['smart_auto_order_enabled'] = d.smart_auto_order_enabled
    if (d.status !== undefined) payload['status'] = d.status
    if (d.notification_channels !== undefined) payload['notification_channels'] = d.notification_channels
    return payload
  }
}

export const employeeApi = new EmployeeApiService()
export default employeeApi

export type {
  Employee,
  EmployeeFilters,
  EmployeeFormData,
  EmployeeStatsData,
  PaginatedEmployeeResponse
}
